import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Building2,
  Lock,
  FileSpreadsheet,
  Scale,
  Activity,
  UserCheck,
  ChevronDown,
  Download,
  PhoneCall,
  HelpCircle,
  Users,
  FileText,
  Landmark,
  HeartHandshake,
  Cpu,
  Layers,
  ClipboardCheck,
} from "lucide-react";

const IMG = {
  boardroom: "https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGV4ZWN1dGl2ZSUyMHRlYW0lMjBtZWV0aW5nJTIwYm9hcmRyb29tfGVufDB8fHx8MTc5MDU1MDEzM3ww&ixlib=rb-4.1.0&q=85",
  execs: "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGV4ZWN1dGl2ZSUyMHRlYW0lMjBtZWV0aW5nJTIwYm9hcmRyb29tfGVufDB8fHx8MTc5MDU1MDEzM3ww&ixlib=rb-4.1.0&q=85",
  tower: "https://images.unsplash.com/photo-1785844292846-31e526f8731a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBza3lzY3JhcGVyJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBqYXBhbnxlbnwwfHx8fDE3OTA1NTAxMzN8MA&ixlib=rb-4.1.0&q=85",
};

const capabilities = [
  { icon: Landmark, title: "統合会計基盤 & 複式簿記", desc: "総勘定元帳・試算表・P/L・B/Sをリアルタイムに自動連携。複数拠点・部門別管理に対応した堅牢な会計基盤。" },
  { icon: FileText, title: "請求管理 Quote-to-Cash", desc: "見積・受注・納品・請求・入金・領収までを一気通貫で処理。未収金の回収状況を可視化します。" },
  { icon: Cpu, title: "AI 銀行明細 自動照合", desc: "CSV・API連携で明細を自動仕訳。学習辞書エンジンが経理担当者の手作業を継続的に削減します。" },
  { icon: FileSpreadsheet, title: "適格請求書 / 消費税集計", desc: "10%・8%軽減税率の税率別集計と端数処理、申告書用の集計表・付表をワンクリックで出力。" },
  { icon: Layers, title: "固定資産 & 減価償却", desc: "定額法・定率法・少額資産・一括償却・除却/売却まで、税務基準に準拠して自動追跡します。" },
  { icon: HeartHandshake, title: "国保連（障害福祉）請求", desc: "障害福祉サービス固有の国保連請求、支払決定通知PDFの自動取込と請求差額の自動照合に対応。" },
  { icon: UserCheck, title: "権限管理 (RBAC) & 内部統制", desc: "ロールベースのアクセス制御、多層承認ワークフロー、全操作の監査ログを永続保持します。" },
  { icon: Download, title: "帳票 PDF / Excel 出力", desc: "顧問税理士・監査法人・金融機関への提出用フォーマットを即時に出力。決算・監査対応を効率化。" },
  { icon: ShieldCheck, title: "国内最高水準のセキュリティ", desc: "国内データセンター保管、TLS 1.3 通信、常時バックアップとBCP対策で事業継続を支えます。" },
];

const governance = [
  { icon: ClipboardCheck, title: "監査ログ（証蹟管理）", desc: "いつ・誰が・どのデータを変更したかを改ざん困難な形式で永続的に記録します。" },
  { icon: Scale, title: "多層承認ワークフロー", desc: "決裁権限に応じた複数段階の承認プロセスを標準装備し、内部統制を強化します。" },
  { icon: Lock, title: "銀行レベルの暗号化", desc: "通信・保存データを二重に暗号化。機微な財務データを厳格に保護します。" },
  { icon: FileText, title: "法令完全適合", desc: "電子帳簿保存法（優良な電子帳簿要件）および適格請求書保存方式に準拠。" },
  { icon: Activity, title: "24/365 監視 & BCP", desc: "障害・災害時の復旧プロトコルと常時監視体制で高い可用性を維持します。" },
  { icon: Building2, title: "国内データセンター保管", desc: "データは国内の堅牢なデータセンターに保管。ガバナンス要件に応えます。" },
];

const metrics = [
  { num: "99.99%", label: "SLA 稼働率保証" },
  { num: "85%", label: "月次決算 工数削減" },
  { num: "1,200社+", label: "導入企業・福祉事業者" },
  { num: "100%", label: "電帳法・インボイス対応" },
];

const plans = [
  {
    name: "法人標準", en: "Standard Corporate", price: "¥9,800", period: "月額（税別）",
    desc: "成長中の企業・中小事業所に最適な標準構成。", testid: "pricing-individual-cta", cta: "無料で試す", highlight: false,
    features: ["請求書 無制限発行", "AI自動仕訳・銀行明細連携", "インボイス・電帳法対応", "P/L・B/S・試算表 自動生成", "標準サポート"],
  },
  {
    name: "エンタープライズ", en: "Enterprise Pro", price: "¥29,800", period: "月額（税別）",
    desc: "複数部門・高度な権限設定・内部統制が必要な組織向け。", testid: "pricing-sme-cta", cta: "デモを予約する", highlight: true, badge: "一番人気・大企業推奨",
    features: ["法人標準の全機能", "ロール別アクセス権限 (RBAC)", "多層承認ワークフロー", "固定資産・減価償却管理", "専任カスタマーサクセス"],
  },
  {
    name: "福祉・国保連特化", en: "Welfare & Public", price: "個別見積", period: "要お問い合わせ",
    desc: "障害福祉・就労支援事業者向けの特別構成。", testid: "pricing-welfare-cta", cta: "相談する", highlight: false,
    features: ["国保連 支払決定通知PDF 自動取込", "給付費集計・請求差額 自動チェック", "監査用帳票 一括出力", "SLA保証 & 専任オンボーディング"],
  },
];

const faqs = [
  { q: "セキュリティ審査（情報セキュリティチェックシート）に対応できますか？", a: "はい。暗号化方式、データ保管場所、アクセス制御、監査ログ、BCP等について、貴社の調達・情報システム部門向けにご回答資料を提出いたします。" },
  { q: "顧問税理士・監査法人にデータを共有できますか？", a: "権限を分離した閲覧アカウントを発行できます。試算表・総勘定元帳・P/L・B/SはPDF/Excelでワンクリック出力でき、監査対応を効率化します。" },
  { q: "既存会計システムからのデータ移行は可能ですか？", a: "勘定科目・開始残高・取引データのインポートに対応しています。専任担当が移行計画の策定から本稼働まで伴走します。" },
  { q: "障害福祉（国保連）の請求業務にも使えますか？", a: "はい。国保連請求の作成、支払決定通知書PDFの自動取込、請求額との差額（査定・返戻）照合、給付費収入の会計連携まで一体で対応します。" },
  { q: "複数拠点・複数部門の管理はできますか？", a: "エンタープライズプランでは部門別管理、ロール別アクセス権限、多層承認ワークフローにより、組織全体のガバナンスを担保します。" },
];

function Faq({ item, open, onToggle, idx }) {
  return (
    <div className="border-b border-slate-200" data-testid={`faq-item-${idx}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display font-semibold text-slate-900 text-base sm:text-lg flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" strokeWidth={1.75} />
          {item.q}
        </span>
        <ChevronDown className={`h-5 w-5 text-slate-400 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-[max-height,opacity] duration-300 ${open ? "max-h-60 opacity-100" : "max-h-0 opacity-0"}`}>
        <p className="pb-5 pl-8 text-sm text-slate-600 leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const [faqOpen, setFaqOpen] = useState(0);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/logo.png" alt="PRC会計 SaaS" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-bold text-slate-900 text-lg">PRC会計 SaaS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Enterprise Accounting Platform</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#capabilities" className="link-underline hover:text-slate-900">統合会計機能</a>
            <a href="#governance" className="link-underline hover:text-slate-900">ガバナンス・セキュリティ</a>
            <a href="#welfare" className="link-underline hover:text-slate-900">福祉・国保連</a>
            <a href="#cases" className="link-underline hover:text-slate-900">導入実績</a>
            <a href="#pricing" className="link-underline hover:text-slate-900">料金プラン</a>
          </nav>
          <div className="flex items-center gap-2">
            <a href="#contact" className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-testid="header-doc-request">
              <Download className="h-4 w-4" /> 資料請求
            </a>
            <Link to="/login" data-testid="header-login-link" className="hidden sm:inline-flex items-center px-3.5 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors">
              ログイン
            </Link>
            <Link to="/register" data-testid="header-register-link" className="inline-flex items-center justify-center rounded-md bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800">
              無料トライアル
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <img src={IMG.tower} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.18] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-7">
            <div className="fade-up fade-up-1 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3.5 py-1.5 text-xs font-medium text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
              経理DX ・ 内部統制 ・ インボイス / 電帳法 / 国保連請求 完全対応
            </div>
            <h1 className="fade-up fade-up-2 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight">
              大企業の財務統制を、<br />
              <span className="text-emerald-400">すべての組織へ。</span>
            </h1>
            <p className="fade-up fade-up-3 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              複式簿記、Quote-to-Cash、AI自動照合、固定資産、障害福祉（国保連）請求まで。
              エンタープライズの複雑な会計業務を一元化し、ガバナンスと意思決定スピードを最大化する次世代統合クラウド会計基盤です。
            </p>
            <div className="fade-up fade-up-4 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-400 hover:-translate-y-0.5">
                デモを予約 / 14日間無料 <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="inline-flex items-center gap-2 rounded-md border border-slate-600 bg-white/5 px-6 py-3 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/10 transition-colors">
                ログイン
              </Link>
              <a href="#contact" data-testid="hero-cta-doc" className="inline-flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-300 hover:text-white transition-colors">
                <Download className="h-4 w-4" /> サービス資料
              </a>
            </div>
            <div className="fade-up fade-up-5 flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 text-xs text-slate-400">
              {["ISMS（ISO27001）準拠", "256bit暗号化", "電子帳簿保存法 適合", "国内データセンター保管"].map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2} /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 fade-up fade-up-3">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-emerald-500/10 blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/10">
                <img src={IMG.boardroom} alt="経営会議で財務データを確認する役員チーム" className="w-full h-[340px] sm:h-[420px] object-cover" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-900/90 to-transparent p-5">
                  <div className="flex items-center gap-3 text-white">
                    <div className="rounded-lg bg-emerald-500/20 p-2 backdrop-blur"><Activity className="h-5 w-5 text-emerald-300" /></div>
                    <div>
                      <div className="font-display font-semibold text-sm">リアルタイム経営ダッシュボード</div>
                      <div className="text-[11px] text-slate-300">全社の売上・利益・資金繰りを一画面で把握</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust metrics strip */}
      <section className="bg-slate-950 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {metrics.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-numbers font-bold text-3xl sm:text-4xl text-emerald-400">{s.num}</div>
              <div className="mt-1.5 text-xs tracking-[0.12em] font-semibold text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pb-10">
          <div className="text-center text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">製造業・ITサービス・医療福祉法人・上場グループ企業に選ばれています</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
            {["SEIWA HOLDINGS", "NIPPON TECHNO", "AOZORA GROUP", "KYOWA WELFARE", "MEISHIN CORP", "TOA SYSTEMS"].map((n) => (
              <span key={n} className="font-display font-bold text-slate-400 text-sm sm:text-base tracking-wide">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities bento */}
      <section id="capabilities" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-3">Core Capabilities</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight leading-[1.2]">
              組織の会計業務を、ひとつの基盤に統合する。
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              請求・経費・仕訳・固定資産・国保連請求・レポートまで。分断されがちな業務を横断的に連携し、
              全社の数字を単一の信頼できる基盤で管理します。
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((f, i) => (
              <div key={f.title} data-testid={`feature-card-${i}`}
                className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_2px_20px_rgba(15,23,42,0.03)] hover:shadow-[0_16px_44px_rgba(15,23,42,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300">
                <div className="h-11 w-11 rounded-xl bg-slate-900 flex items-center justify-center text-emerald-400 mb-5 transition-colors group-hover:bg-emerald-700 group-hover:text-white">
                  <f.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-bold text-slate-900 text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance & Security */}
      <section id="governance" className="relative overflow-hidden bg-slate-900 py-20 sm:py-28">
        <div className="absolute inset-0 bg-grain opacity-[0.4]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400 mb-3">Security & Governance</div>
              <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight leading-[1.2]">
                内部統制と<br />セキュリティを標準装備。
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed text-sm">
                大企業の調達・情報システム部門・監査法人の要求水準に応える、証蹟管理・権限分離・暗号化・法令適合を
                プラットフォームの中核として提供します。
              </p>
              <a href="#contact" className="mt-6 inline-flex items-center gap-2 rounded-md border border-emerald-400/40 bg-emerald-400/10 px-5 py-2.5 text-sm font-medium text-emerald-300 hover:bg-emerald-400/20 transition-colors" data-testid="governance-sheet-cta">
                <ShieldCheck className="h-4 w-4" /> セキュリティチェックシート請求
              </a>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {governance.map((g) => (
                <div key={g.title} className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 hover:bg-white/[0.07] transition-colors">
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="rounded-lg bg-emerald-500/15 p-2 text-emerald-300"><g.icon className="h-5 w-5" strokeWidth={1.75} /></div>
                    <h3 className="font-display font-semibold text-white text-base">{g.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Welfare specialization */}
      <section id="welfare" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-3">Sector Specialization</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight leading-[1.2]">
              障害福祉・就労支援に特化した<br />国保連請求ワークフロー。
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              国保連請求データの作成から、支払決定通知書PDFの自動取込・解析、請求額との差額（査定・返戻）照合、
              給付費収入の会計仕訳連携までを一体化。福祉現場と会計事務所のコミュニケーション負担を大幅に軽減します。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "支払決定通知（PDF）をAIで読み取り、請求と自動突合",
                "査定・返戻を反映した給付費収入の会計連携",
                "国保連（給付費）と自主事業（就労支援事業収入）の勘定を厳密に分離",
                "監査・行政指導に備えた帳票の一括出力",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
              <img src={IMG.execs} alt="福祉法人の財務・経理チーム" className="w-full h-[380px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Case study */}
      <section id="cases" className="py-20 sm:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-8 text-center">Customer Success</div>
          <div className="rounded-3xl bg-white border border-slate-200 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.18)] p-8 sm:p-12">
            <blockquote className="font-display text-xl sm:text-2xl lg:text-3xl text-slate-900 leading-relaxed tracking-tight text-center max-w-3xl mx-auto">
              「拠点ごとにばらばらだった会計と国保連請求が完全に一元化されました。
              月次決算の確定が <span className="text-emerald-700">12日短縮</span>し、監査対応も大幅に効率化できています。」
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center text-emerald-400"><Users className="h-6 w-6" /></div>
              <div className="text-left">
                <div className="font-semibold text-slate-900">関口 拓也 氏 — 財務経理部長</div>
                <div className="text-sm text-slate-500">社会福祉法人 創和会（従業員数 450名）</div>
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-slate-100 pt-8">
              {[
                { n: "▲12日", l: "月次決算 短縮" },
                { n: "6拠点", l: "会計を一元化" },
                { n: "▲70%", l: "国保連 照合工数" },
                { n: "0件", l: "監査指摘（導入後）" },
              ].map((s) => (
                <div key={s.l} className="text-center">
                  <div className="font-numbers font-bold text-2xl sm:text-3xl text-slate-900">{s.n}</div>
                  <div className="mt-1 text-xs text-slate-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-3">Pricing</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight">組織の規模に合わせて、透明な料金体系。</h2>
            <p className="mt-4 text-slate-600">まずは14日間無料。クレジットカードの登録は不要です。エンタープライズは個別のご要件に合わせて構成します。</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {plans.map((p) => (
              <div key={p.name}
                className={`relative flex flex-col rounded-2xl p-8 transition-transform duration-300 hover:-translate-y-1 ${p.highlight ? "bg-slate-900 text-white border-2 border-emerald-500 shadow-[0_24px_60px_-20px_rgba(5,150,105,0.5)]" : "bg-white text-slate-900 border border-slate-200"}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-8 bg-emerald-500 text-slate-900 text-[10px] uppercase tracking-[0.12em] font-bold px-3 py-1 rounded">{p.badge}</div>
                )}
                <div className={`text-xs uppercase tracking-[0.15em] font-semibold ${p.highlight ? "text-emerald-300" : "text-slate-500"}`}>{p.en}</div>
                <div className="mt-1 font-display font-bold text-xl">{p.name}</div>
                <div className="mt-4 font-numbers font-bold text-4xl">{p.price}<span className={`text-sm font-normal ml-1 ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>/ {p.period}</span></div>
                <p className={`mt-3 text-sm ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.desc}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex gap-2 text-sm ${p.highlight ? "text-slate-200" : "text-slate-700"}`}>
                      <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${p.highlight ? "text-emerald-400" : "text-emerald-600"}`} strokeWidth={2} />{f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid={p.testid}
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold transition-colors ${p.highlight ? "bg-emerald-500 text-slate-900 hover:bg-emerald-400" : "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50"}`}>
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-3">FAQ</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight">導入・調達に関するご質問</h2>
          </div>
          <div>
            {faqs.map((item, i) => (
              <Faq key={i} idx={i} item={item} open={faqOpen === i} onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA banner */}
      <section id="contact" className="py-20 sm:py-28 bg-slate-950">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15]">
            財務基盤の刷新を、<span className="text-emerald-400">今日から</span>。
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            専任担当が貴社の会計・請求・内部統制の課題をヒアリングし、最適な導入プランをご提案します。
            まずは個別デモまたはサービス資料からお気軽にご相談ください。
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" data-testid="final-cta-register" className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 hover:-translate-y-0.5 transition-all">
              デモを予約する <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#" data-testid="final-cta-doc" className="inline-flex items-center gap-2 rounded-md border border-slate-600 bg-white/5 px-7 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors">
              <Download className="h-4 w-4" /> サービス資料をダウンロード
            </a>
            <a href="tel:0300000000" data-testid="final-cta-tel" className="inline-flex items-center gap-2 px-5 py-3.5 text-sm font-medium text-slate-300 hover:text-white transition-colors">
              <PhoneCall className="h-4 w-4" /> 03-XXXX-XXXX（平日 9-18時）
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo.png" alt="PRC会計 SaaS" className="h-9 w-9 object-contain" />
                <div className="font-display font-bold text-white">PRC会計 SaaS</div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">日本の企業・福祉事業者のためのエンタープライズ統合クラウド会計基盤。</p>
            </div>
            {[
              { h: "会社情報", items: ["運営会社（PRC Financial Solutions K.K.）", "特定商取引法に基づく表記", "採用情報"] },
              { h: "法務・セキュリティ", items: ["プライバシーポリシー", "情報セキュリティ基本方針（ISMS）", "利用規約"] },
              { h: "お問い合わせ", items: ["導入相談窓口", "サポートセンター", "03-XXXX-XXXX（平日 9-18時）"] },
            ].map((col) => (
              <div key={col.h}>
                <div className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-4">{col.h}</div>
                <ul className="space-y-2.5">
                  {col.items.map((it) => (
                    <li key={it}><a href="#" className="text-sm text-slate-300 hover:text-white transition-colors">{it}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-slate-500">© 2026 PRC会計 SaaS (PRC Financial Solutions K.K.). All rights reserved.</div>
            <div className="flex items-center gap-4 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> ISMS 準拠</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-emerald-500" /> 256bit 暗号化</span>
              <span className="inline-flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-emerald-500" /> 国内保管</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
